import {useEffect, useCallback, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {trackEvent, trackPageView, timeEvent} from '../analytics'
import {Dict} from 'mixpanel-browser'

/**
 * Hook for tracking analytics events in components
 * Provides convenient methods for common tracking scenarios
 */
export const useAnalytics = () => {
  const location = useLocation()

  // Track event with properties
  const track = useCallback((eventName: string, properties?: Dict) => {
    trackEvent(eventName, properties)
  }, [])

  // Track page view on route change
  useEffect(() => {
    trackPageView()
  }, [location.pathname])

  return {
    track,
    trackPageView,
    timeEvent,
  }
}

/**
 * Hook for tracking when a component mounts/unmounts
 */
export const useTrackComponentLifecycle = (
  componentName: string,
  properties?: Dict
) => {
  const mountTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    // Track component mount
    trackEvent(`${componentName}_mounted`, properties)

    // Track component unmount and time spent
    return () => {
      const timeSpent = Date.now() - mountTimeRef.current
      trackEvent(`${componentName}_unmounted`, {
        ...properties,
        timeSpent: Math.round(timeSpent / 1000), // Convert to seconds
      })
    }
  }, [componentName, properties])
}

/**
 * Hook for tracking form submissions
 */
export const useTrackForm = (formName: string) => {
  const track = useCallback(
    (action: 'started' | 'submitted' | 'validation_error' | 'abandoned', properties?: Dict) => {
      trackEvent(`form_${action}`, {
        formName,
        ...properties,
      })
    },
    [formName]
  )

  return track
}

/**
 * Hook for tracking modal interactions
 */
export const useTrackModal = (modalName: string) => {
  const track = useCallback(
    (action: 'opened' | 'closed' | 'submitted', properties?: Dict) => {
      trackEvent(`modal_${action}`, {
        modalName,
        ...properties,
      })
    },
    [modalName]
  )

  return track
}

/**
 * Hook for tracking table/list interactions
 */
export const useTrackList = (listName: string) => {
  const track = useCallback(
    (
      action: 'filtered' | 'sorted' | 'searched' | 'item_clicked' | 'page_changed',
      properties?: Dict
    ) => {
      trackEvent(`list_${action}`, {
        listName,
        ...properties,
      })
    },
    [listName]
  )

  return track
}

/**
 * Hook for tracking user actions with timing
 * Automatically tracks start time when component mounts
 */
export const useTrackTimed = (eventName: string) => {
  useEffect(() => {
    timeEvent(eventName)
  }, [eventName])

  const complete = useCallback(
    (properties?: Dict) => {
      trackEvent(eventName, properties)
    },
    [eventName]
  )

  return {complete}
}
